// =============================================================
// config.ts — 모든 체인/컨트랙트 의존성의 단일 교체 지점
//  · 환경변수(.env)로 주소/체인 주입 가능 (VITE_ 접두사)
//  · 없으면 아래 기본값 사용
// =============================================================

export interface ChainConfig {
  id: number;
  idHex: string;
  name: string;
  rpc: string;
  explorer: string;
  nativeCurrency: { name: string; symbol: string; decimals: number };
}

const env = import.meta.env;

// 기본은 BSC mainnet. 테스트넷이면 .env 에서 VITE_CHAIN=testnet
const isTestnet = (env.VITE_CHAIN ?? '').toLowerCase() === 'testnet';

export const CHAIN: ChainConfig = isTestnet
  ? {
      id: 97,
      idHex: '0x61',
      name: 'BSC Testnet',
      rpc: env.VITE_RPC ?? 'https://data-seed-prebsc-1-s1.binance.org:8545',
      explorer: 'https://testnet.bscscan.com',
      nativeCurrency: { name: 'tBNB', symbol: 'tBNB', decimals: 18 },
    }
  : {
      id: 56,
      idHex: '0x38',
      name: 'BNB Smart Chain',
      rpc: env.VITE_RPC ?? 'https://bsc-dataseed.binance.org',
      explorer: 'https://bscscan.com',
      nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
    };

// ★ 배포 후 .env 의 VITE_PV_GATE_ADDRESS 로 주입 (또는 여기 직접) ★
export const PV_GATE_ADDRESS: string =
  env.VITE_PV_GATE_ADDRESS ?? '0x0000000000000000000000000000000000000000';

// Reown AppKit Project ID (https://dashboard.reown.com). injected 어댑터만 쓸 땐 불필요.
export const REOWN_PROJECT_ID: string = env.VITE_REOWN_PROJECT_ID ?? '';

// 자격 임계값 (정수 MOL 단위, 18dec 곱셈은 사용처에서)
export const PV_THRESHOLD = 20_000_000n; // Dener 서임선 = 2천만 MOL

export const ZERO = '0x0000000000000000000000000000000000000000';
export const ONE = 10n ** 18n;

// 최소 ABI (배포 ABI 시그니처와 일치 확인 필요)
export const PV_GATE_ABI = [
  'function swap(address distributor, uint256 minMolOut) payable',
  'function swapNoDistributor(uint256 minMolOut) payable',
  'function quote(uint256 bnbIn, address distributor) view returns (uint256 molOut, uint256 baseMol, uint256 reward, uint256 toTreasury, uint16 disc)',
  'function molForBnb(uint256 bnbIn) view returns (uint256)',
  'function bnbForMol(uint256 molAmount) view returns (uint256)',
  'function bnbUsd8() view returns (uint256)',
  'function molPriceUsd8() view returns (uint256)', // public state getter (FIXED 고정가)
  'function molPrice8() view returns (uint256)',    // 모드 반영 실시간가 (FIXED/FEED)
  'function effectiveRates(address dist) view returns (uint16 discountBp, uint16 rewardBp)',
  'function distributorInfo(address a) view returns (bool exists, bool active, uint16 discountBp, uint16 rewardBp, bool ownerSet)',
  'function molStock() view returns (uint256)',
  'function mol() view returns (address)',
  'function paused() view returns (bool)',
  'function MIN_MOL() view returns (uint256)',
  'function owner() view returns (address)',
  'function treasury() view returns (address)',
  'function distributorCount() view returns (uint256)',
  'function distributorAt(uint256 i) view returns (address)',
  'function registerDistributor(address dist)',
  'function setActive(address dist, bool active)',
  'function ownerSetRates(address dist, uint16 discountBp, uint16 rewardBp)',
  'function releaseToDistributor(address dist)',
  'function removeDistributor(address dist)',
  'function setMyDiscount(uint16 discountBp)',
  'function autoReward(uint16 discountBp) pure returns (uint16)',
  'event Swapped(address indexed buyer, uint256 bnbIn, uint256 molOut, address indexed distributor, uint16 discountBp, uint256 reward, uint256 toTreasury)',
] as const;

export const ERC20_ABI = [
  'function balanceOf(address) view returns (uint256)',
] as const;